import type firebase from 'firebase/compat/app'
import { firestore, auth, storage } from '@utils/firebase'
import { getUsersName } from '@utils/strings'
import { User } from '@Interfaces/Data/UserInterface'
import { Org } from '@Interfaces/Data/OrganizationInterface'
import { Post, LumensData } from '@Interfaces/Data/ContentInterfaces'
import { ApiResponse } from '@Interfaces/Api/ResponsesInterface'

/**
 * Get post by ID
 */
export const getPostById = async (docId: string): Promise<ApiResponse<Post | null>> => {
  try {
    const doc = await firestore.collection('posts').doc(docId).get()

    if (doc.exists) {
      const data = doc.data()
      if (!data) {
        return {
          success: true,
          data: null,
        }
      }
      const post: Post = {
        id: doc.id,
        postId: doc.id,
        ...data,
      }

      return {
        success: true,
        data: post,
      }
    } else {
      return {
        success: true,
        data: null,
      }
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get post',
      data: undefined,
    }
  }
}

/**
 * Get user's posts
 */
export const getUsersPosts = async (user: User): Promise<ApiResponse<Post[]>> => {
  try {
    const querySnapshot = await firestore
      .collection('posts')
      .where('poster', '==', user.id)
      .orderBy('createdAt', 'desc')
      .get()

    const temp: Post[] = []
    querySnapshot.forEach((doc) => {
      const data = doc.data()
      const post: Post = {
        id: doc.id,
        postId: doc.id,
        ...data,
      }
      temp.push(post)
    })

    return {
      success: true,
      data: temp,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get user posts',
      data: undefined,
    }
  }
}

/**
 * Get organization's posts
 */
export const getOrgsPosts = async (
  org: Org
): Promise<
  ApiResponse<{
    posts: Post[]
    lastDoc: firebase.firestore.QueryDocumentSnapshot | null
  }>
> => {
  try {
    const querySnapshot = await firestore
      .collection('posts')
      .where('orgId', '==', org.id)
      .orderBy('createdAt', 'desc')
      .get()

    const temp: Post[] = []
    let lastDoc: firebase.firestore.QueryDocumentSnapshot | null = null
    querySnapshot.forEach((doc) => {
      const data = doc.data()
      const post: Post = {
        id: doc.id,
        postId: doc.id,
        ...data,
      }
      temp.push(post)
      lastDoc = doc
    })

    return {
      success: true,
      data: {
        posts: temp,
        lastDoc,
      },
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get organization posts',
      data: undefined,
    }
  }
}

/**
 * Get lumens data
 */
export async function getLumens(
  feedId: string,
  post: Post
): Promise<
  ApiResponse<{
    totalLumens: number
    lumens: number
    lumensLastSaved: number
    max: number
  }>
> {
  try {
    const doc = await firestore.collection('lumens').doc(feedId).get()

    if (doc.exists) {
      const data = doc.data() as Partial<LumensData>
      const totalLumens =
        typeof post.lumens === 'number' ? post.lumens : Number(post.lumens ?? 0)
      const lumens = typeof data.lumens === 'number' ? data.lumens : Number(data.lumens ?? 0)
      const lumensLastSaved = lumens
      const max = totalLumens + 50 - lumens

      return {
        success: true,
        data: {
          totalLumens,
          lumens,
          lumensLastSaved,
          max,
        },
      }
    } else {
      const fallbackLumens =
        typeof post.lumens === 'number' ? post.lumens : Number(post.lumens ?? 0)
      return {
        success: true,
        data: {
          totalLumens: fallbackLumens,
          lumens: 0,
          lumensLastSaved: 0,
          max: fallbackLumens + 50,
        },
      }
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get lumens',
      data: undefined,
    }
  }
}

/**
 * Update lumens
 */
export const updateLumens = async (
  updatedLumenCount: number,
  max: number,
  lumensLastSaved: number,
  feedId: string,
  post: Post,
  currentUser: User
): Promise<ApiResponse<boolean>> => {
  try {
    if (updatedLumenCount <= max && updatedLumenCount !== lumensLastSaved) {
      await firestore
        .collection('lumens')
        .doc(feedId)
        .set(
          {
            lumens: updatedLumenCount,
            poster: post.poster,
            postId: post.postId,
            sendersName: getUsersName(currentUser),
            userId: currentUser.id,
            updatedAt: new Date(),
          },
          { merge: true }
        )

      return {
        success: true,
        data: true,
      }
    }

    return {
      success: false,
      error: 'Lumen count invalid or unchanged',
      data: false,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to update lumens',
      data: undefined,
    }
  }
}
